import { useState, useCallback } from 'react';

interface GeolocationPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface UseGeolocationReturn {
  position: GeolocationPosition | null;
  error: string | null;
  loading: boolean;
  permissionDenied: boolean;
  getCurrentPosition: () => Promise<GeolocationPosition>;
  clearError: () => void;
}

// Fallback координаты для Алматы
const ALMATY_FALLBACK = {
  latitude: 43.2220,
  longitude: 76.8512,
  accuracy: 1000, // низкая точность для fallback
};

export const useGeolocation = (): UseGeolocationReturn => {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const getCurrentPosition = useCallback((): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errorMsg = 'Геолокация не поддерживается вашим браузером. Используются координаты по умолчанию.';
        console.warn(errorMsg);
        setError(errorMsg);
        setPosition(ALMATY_FALLBACK);
        resolve(ALMATY_FALLBACK);
        return;
      }

      setLoading(true);
      setError(null);
      setPermissionDenied(false);

      // Увеличиваем timeout и отключаем высокую точность для более стабильной работы
      const options: PositionOptions = {
        enableHighAccuracy: false, // Отключаем для более быстрого ответа
        timeout: 15000, // Увеличиваем timeout до 15 секунд
        maximumAge: 300000, // Кэш на 5 минут
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };

          console.log('Геолокация получена успешно:', coords);
          setPosition(coords);
          setLoading(false);
          setPermissionDenied(false);
          resolve(coords);
        },
        (error) => {
          let errorMessage = 'Ошибка получения местоположения';
          let shouldUseFallback = false;
          let isDenied = false;

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Доступ к геолокации запрещен. Используются координаты по умолчанию.';
              shouldUseFallback = true;
              isDenied = true;
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Информация о местоположении недоступна. Используются координаты по умолчанию.';
              shouldUseFallback = true;
              break;
            case error.TIMEOUT:
              errorMessage = 'Превышено время ожидания. Используются координаты по умолчанию.';
              shouldUseFallback = true;
              break;
          }

          console.warn(`Геолокация недоступна (код ${error.code}):`, error.message);
          console.log('Используются fallback координаты:', ALMATY_FALLBACK);

          setError(errorMessage);
          setLoading(false);
          setPermissionDenied(isDenied);

          if (shouldUseFallback) {
            // Используем fallback вместо reject
            setPosition(ALMATY_FALLBACK);
            resolve(ALMATY_FALLBACK);
          } else {
            reject(new Error(errorMessage));
          }
        },
        options
      );
    });
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setPermissionDenied(false);
  }, []);

  return {
    position,
    error,
    loading,
    permissionDenied,
    getCurrentPosition,
    clearError,
  };
};